import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  addDiscount,
  addProductToCart,
  getCart,
  removeProductFromCart,
} from "@/queries/cart"
import type { CartItem } from "@/types/shop"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  ArrowRight,
  Camera,
  Loader2,
  Minus,
  Plus,
  ShoppingBag,
} from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { AxiosError } from "axios"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
export const Route = createFileRoute("/shop/cart/")({
  component: ShoppingCart,
})

function ShoppingCart() {
  const [cart, setCart] = useState<Array<CartItem>>([])
  const [details, setCartDetails] = useState<null | {finalPrice: number, totalDiscount: number, price: number,totalSale?:number}>(null)
  const [discountValue, setDiscountValue] = useState<string>('')
  const [activeDiscount, setActiveDiscount] = useState('')


  const getCartQuery = useQuery({
    queryFn: () => getCart(),
    queryKey: ["shoping-cart"],
  })


  useEffect(() => {
    if (getCartQuery.data) {
      setActiveDiscount(getCartQuery.data.data.cart.discount)
      if (getCartQuery.data.data.cart.discount == null) {
        setDiscountValue('')
      }
      setCart(Object.values(getCartQuery.data.data.cart.items))
      setCartDetails(getCartQuery.data.data.cart.details)
    }
  }, [getCartQuery.data])



  const addDiscountMutation = useMutation({
    mutationFn: (discount: string) => addDiscount(discount),
    mutationKey: ['add', 'discount'],
    onSuccess() {
      toast.success('کد تخفیف با موفقیت اعمال شد')
      getCartQuery.refetch()
    },
    onError() {
      toast.error('خطا در اعمال کد تخفیف')
    }
  })

  const addCartMutation = useMutation({
    mutationFn: ({
      product_id,
      cart_key,
    }: {
      product_id: number
      cart_key?: any
    }) => addProductToCart(product_id, cart_key),
    onSuccess({data}) {
      setCartDetails(data.cart.details)
      setCart(Object.values(data.cart.items))
    },
    onError(error: AxiosError<{ message: string }>) {
      if (error.response?.data?.message.includes("no item in warehouse")) {
        toast.error("محصول کافی در انبار موجود نمی باشد")
      }
    },
  })

  const removeCartMutation = useMutation({
    mutationFn: ({ product_id }: { product_id: number; cart_key?: any }) =>
      removeProductFromCart(product_id),
    onSuccess({data}) {
      setCartDetails(data.cart.details)
      setCart(Object.values(data.cart.items))
    },
  })
  


  return (
    <div>
      <div className="flex items-center py-10">
        <Link to="/shop">
          <Button variant="ghost" size="icon">
            <ArrowRight />
          </Button>
        </Link>
        <h1 className="text-xl font-bold">سبد خرید شما </h1>
      </div>

      <Separator />

      {getCartQuery.isFetching ? (
        <>
          <div>
            <Loader2 className="animate-spin duration-1000" />
          </div>
        </>
      ) : cart.length < 1 ? (
        <div className="flex flex-col h-80 justify-center items-center gap-4">
          <ShoppingBag size={50} />
          <p className="text-xl">سبد خرید شما خالی است</p>
          <Link to="/shop">
            <Button>بازگشت به فروشگاه</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-8 mt-4">
          <div className="col-span-8 md:col-span-5 flex flex-col gap-4 divide divide-border">
            {cart.map((item) => (
              <div
                key={item.product.id}
                className="flex flex-col border-b border-dashed border-foreground"
              >
                <div className="flex gap-4">
                  <div className="size-44 flex justify-center items-center bg-muted">
                    <Camera />
                  </div>

                  <div className="flex flex-col gap-4">
                    <p className="text-xl font-bold">{item.product.name}</p>
                    
                    <p className={`${item.discount_price && 'line-through text-sm'}`}>{Intl.NumberFormat().format(item.product.price)} ریال</p>
                    {item.discount_price && <p>
                        {Intl.NumberFormat().format(item.final_unit_price || 0)} ریال
                      </p>}
                    

                    <div className="flex justify-between items-center bg-secondary rounded-lg w-32">
                      <Button
                        disabled={item.disableAdd}
                        size="icon"
                        onClick={() =>
                          addCartMutation.mutate({
                            product_id: item.product.id,
                          })
                        }
                      >
                        <Plus />
                      </Button>

                      <p>{item?.quantity}</p>

                      <Button
                        size="icon"
                        onClick={() =>
                          removeCartMutation.mutate({
                            product_id: item.product.id,
                          })
                        }
                      >
                        <Minus />
                      </Button>
                    </div>
                    {item.disableAdd && (
                      <p className="text-sm text-red-600">
                        موجودی انبار کافی نمی باشد
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center px-4 py-2">
                  <p className="">مجموع کالا : </p>
                  <div className="flex flex-col">
                      {item.discount_price && <>
                        <p className="text-sm line-through">
                          {Intl.NumberFormat().format(item.product.price * item.quantity)}
                        </p>
                      </>}
                  <p className="font-bold text-lg">
                    {Intl.NumberFormat().format(
                      (item.final_unit_price || item.product.price) * item.quantity
                      )}{" "}
                  </p>
                      </div>
                </div>
              </div>
            ))}
          </div>

          <div className="col-span-8 md:col-span-3">
            <Card className="sticky top-50">
              <CardHeader className="flex-row justify-between">
                <CardTitle>سفارش شما</CardTitle>
                {cart.length} کالا
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-2">
                <p className="">جمع کالا ها: </p>
                <p className="text-end">
                  {Intl.NumberFormat().format(
                    details?.price || 0
                  )}{" "}
                  ریال
                </p>

                {details?.totalDiscount && details.totalDiscount > 0 ? 
                <>
                <p>مجموع تخفیف:</p>
                <p className="text-end">
                {Intl.NumberFormat().format(details.totalDiscount)} ریال
                  </p>
                </> : ''
                  }

                {
                  (details?.totalSale && details.totalSale > 0) ? <>
                  <p>تخفیف مناسبتی: </p>
                  <p className="text-end">
                  {Intl.NumberFormat().format(details.totalSale || 0)}
                  </p>
                  </> : ""
                }

                <Separator className="col-span-2" />
                <p>مبلغ پرداختی: </p>
                <p className="text-end">
                {Intl.NumberFormat().format(
                    details?.finalPrice || 0
                  )}{" "}
                  ریال
                </p>
                <Separator className="col-span-2" />
                <div className="flex flex-col gap-2 col-span-2">

                <Label>
                  کد تخفیف
                </Label>

                <div className="flex gap-2">
                <Input value={activeDiscount || discountValue} onChange={(e) => setDiscountValue(e.target.value)} disabled={activeDiscount != null} className={`w-1/2 ${activeDiscount? 'ring-2 ring-green-500' : ''}`} dir="ltr"/>
                {activeDiscount ? <Button onClick={()=> {
                  addDiscountMutation.mutate('')
                }} isLoading={addDiscountMutation.isPending}>
                  حذف کد
                </Button> : <Button onClick={() => {
                  addDiscountMutation.mutate(discountValue)
                }} disabled={!discountValue } isLoading={addDiscountMutation.isPending}>
                  تایید
                </Button>}
                </div>
                </div>

                <div className="col-span-2">
                  <Link to="/shop/logistic" className="col-span-2">
                    <Button className="w-full">ادامه</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}
